import * as T from 'three';
import {weaponWingModuleCount,type WeaponWingForm} from '@hyperlight/shared';

// Presentation shares the catalog's shape and mobility; mechanics stay on the item.
export type CrystalWeaponShape = WeaponWingForm;
export interface CrystalWeaponFrame {
  trigger?:boolean;
  /** Live normalized trigger charge; omitted for remote shot-only animation. */
  charge?:number;
  deployTime?:number;
  stowTime?:number;
  active: boolean;
  /** Aim frame, in render/world space. Origin sits above the trigger hand. */
  aim: T.Matrix4;
  /** Upright player frame, for routing blades around the torso. */
  body: T.Matrix4;
}

const FORWARD = new T.Quaternion().setFromAxisAngle(new T.Vector3(1, 0, 0), Math.PI / 2);
const IDENTITY = new T.Quaternion();
// The upper back pair (0, 3) stays free to support hover / guard on the ground.
// Four back blades become two long receiver rails and the paired stock.
// The four limb blades become the sight, trigger grip, foregrip and inner bore.
// Local -Z is the firing axis. No source mesh is edited or duplicated.
export const CRYSTAL_SNIPER = [
  null,
  [-.063, .018, -.39, .50, 1.44, .68, 1],
  [-.050, .008, .045, .72, .86, .78, 1],
  null,
  [ .063, .018, -.39, .50, 1.44, .68, 1],
  [ .050, .008, .045, .72, .86, .78, 1],
  [0, .120, -.12, .30, .34, .42, 1],
  [0, -.068, .075, .34, .38, .52, 0],
  [0, -.082, -.27, .34, .34, .52, 0],
  [0, .018, -.51, .20, 1.25, .28, 1],
] as const;
export const CRYSTAL_SNIPER_MUZZLE = new T.Vector3(0, .018, -1.09);
export const CRYSTAL_WEAPON_MODULES = [1,2,4,5,6,7,8,9] as const;
export const CRYSTAL_SUPPORT_MODULES = [0,3] as const;
const ROTATIONS = CRYSTAL_SNIPER.map((row,i)=>row?.[6]
  ?new T.Quaternion().setFromAxisAngle(new T.Vector3(0,0,1),i<6?(i<3?-1:1)*Math.PI/2:Math.PI).multiply(FORWARD)
  :IDENTITY.clone());

// THE CRYSTAL BLADE, in the forearm light-blade's own frame (local +Z runs
// elbow → hand → tip, +Y up; the construct's markers still define the hit
// segment). The two lower back pairs laminate edge-on into a segmented blade:
// the middle pair forms the base half, the lowest pair the tip. Each crystal's
// point (-Y) runs toward the tip, its width becomes the blade's height, its
// depth the thickness. The upper pair stays free for flight, as with the gun.
export const CRYSTAL_BLADE = [
  null,
  [-.013,-.045,.177,.45,.82,.22],
  [-.013,-.045,.652,.40,.98,.22],
  null,
  [ .013,-.045,.177,.45,.82,.22],
  [ .013,-.045,.652,.40,.98,.22],
  null,null,null,null,
] as const;
export const CRYSTAL_BLADE_MODULES = [1,2,4,5] as const;
const BLADE_ROTATION = new T.Quaternion().setFromRotationMatrix(new T.Matrix4().makeBasis(
  new T.Vector3(0,1,0),new T.Vector3(0,0,-1),new T.Vector3(-1,0,0)));

// The heavy form borrows ALL ten movable pieces. Six thicker barrels surround
// a wide bore; the former support wings become paired receiver armour, while
// the two remaining limb pieces keep the existing trigger/support grip seats.
const ROTOR_MODULES=[1,2,4,5,8,9];
export const CRYSTAL_MINIGUN_MODULES=[0,1,2,3,4,5,6,7,8,9] as const;
const MINIGUN_ROTOR_RADIUS=.17;
export const CRYSTAL_MINIGUN:(number[]|null)[]=Array.from({length:10},()=>null);
const MINIGUN_ROTATIONS=Array.from({length:10},()=>FORWARD.clone());
for(const [n,i] of ROTOR_MODULES.entries()){
  const angle=n*Math.PI/3;
  CRYSTAL_MINIGUN[i]=[Math.cos(angle)*MINIGUN_ROTOR_RADIUS,.105+Math.sin(angle)*MINIGUN_ROTOR_RADIUS,-.35,.54,1.18,.72];
  MINIGUN_ROTATIONS[i].premultiply(new T.Quaternion().setFromAxisAngle(new T.Vector3(0,0,1),angle));
}
CRYSTAL_MINIGUN[0]=[-.22,.065,.055,1.05,.95,1.7];
CRYSTAL_MINIGUN[3]=[ .22,.065,.055,1.05,.95,1.7];
CRYSTAL_MINIGUN[6]=[0,.12,.28,1.65,.48,1.4];
CRYSTAL_MINIGUN[7]=[-.20,.30,-.08,.95,.28,.8];
MINIGUN_ROTATIONS[7].identity();
const MINIGUN_MUZZLE=new T.Vector3(0,.105,-1.02);

// All forms have ten possible seats. Mobility selects a prefix of whole pairs,
// preserving the upper back pair for last. Unborrowed instances are untouched.
const BORROW_ORDER=[1,2,4,5,6,7,8,9,0,3];
const MODULE_SETS=new Map([4,6,8,10].map(n=>[n,BORROW_ORDER.slice(0,n).sort((a,b)=>a-b)]));
const PDW_ROWS=[
  [-.08,.025,.11,.60,.55,.75],
  [-.042,.015,-.18,.52,.70,.62],
  [-.038,-.012,.045,.58,.48,.64],
  [.08,.025,.11,.60,.55,.75],
  [.042,.015,-.18,.52,.70,.62],
  [.038,-.012,.045,.58,.48,.64],
  [0,.095,-.08,.32,.32,.40],
  [0,-.055,.04,.34,.30,.46],
  [-.06,-.03,-.10,.32,.40,.50],
  [.06,-.03,-.10,.32,.40,.50],
];
const CARBINE_ROWS=[
  [-.095,.02,.12,.72,.62,.88],
  [-.057,.018,-.29,.55,1.02,.72],
  [-.050,.008,.055,.70,.72,.78],
  [.095,.02,.12,.72,.62,.88],
  [.057,.018,-.29,.55,1.02,.72],
  [.050,.008,.055,.70,.72,.78],
  [0,.115,-.12,.30,.34,.42],
  [0,-.068,.075,.34,.38,.52],
  [-.068,-.012,-.23,.38,.56,.68],
  [.068,-.012,-.23,.38,.56,.68],
];
// A wide, open launch tube, paired rear armour and two grounded grip seats.
// It uses the over-shoulder weapon frame, including its existing hand sockets.
const LAUNCHER_ROWS=CRYSTAL_MINIGUN.map(row=>[...row!]);
const LAUNCHER_ROTATIONS=MINIGUN_ROTATIONS.map(q=>q.clone());
for(const [n,i] of ROTOR_MODULES.entries()){
  const a=n*Math.PI/3;
  LAUNCHER_ROWS[i]=[Math.cos(a)*.18,.22+Math.sin(a)*.18,-.20,.65,1.35,1.10];
}
LAUNCHER_ROWS[0]=[-.18,.22,.36,.90,.75,1.4];
LAUNCHER_ROWS[3]=[ .18,.22,.36,.90,.75,1.4];
LAUNCHER_ROWS[6]=[0,-.045,0,.36,.42,.65];
LAUNCHER_ROWS[7]=[0,0,-.205,.40,.40,.60];
LAUNCHER_ROTATIONS[6].identity();LAUNCHER_ROTATIONS[7].identity();
const SNIPER_ROWS=CRYSTAL_SNIPER.map((row,i)=>row??[i===0?-.095:.095,.03,.10,.80,.85,1.05]);
const SNIPER_ROTATIONS=ROTATIONS.map((q,i)=>i===0||i===3?FORWARD.clone():q);
// Additional blade tiers thicken the spine and guard, without changing reach.
const BLADE_ROWS=CRYSTAL_BLADE.map((row,i)=>row??[
  i===0||i===6||i===8?-.035:.035,-.02,i<6?.16:i<8?.30:.66,.32,.55,.30,
]);
interface CrystalLayout {
  rows:readonly (readonly number[])[];
  rotations:readonly T.Quaternion[];
  muzzle:T.Vector3;
  mobility:number;
  deploy:number;
  release:number;
}
const LAYOUTS:Record<CrystalWeaponShape,CrystalLayout>={
  pdw:{rows:PDW_ROWS,rotations:ROTATIONS,muzzle:new T.Vector3(0,.015,-.53),mobility:1,deploy:.18,release:.18},
  carbine:{rows:CARBINE_ROWS,rotations:ROTATIONS,muzzle:new T.Vector3(0,.018,-.79),mobility:.85,deploy:.30,release:.32},
  sniper:{rows:SNIPER_ROWS,rotations:SNIPER_ROTATIONS,muzzle:CRYSTAL_SNIPER_MUZZLE,mobility:.55,deploy:.30,release:.34},
  minigun:{rows:CRYSTAL_MINIGUN as number[][],rotations:MINIGUN_ROTATIONS,muzzle:MINIGUN_MUZZLE,mobility:.18,deploy:.32,release:.32},
  launcher:{rows:LAUNCHER_ROWS,rotations:LAUNCHER_ROTATIONS,muzzle:new T.Vector3(0,.22,-.88),mobility:0,deploy:.50,release:.45},
  blade:{rows:BLADE_ROWS,rotations:BLADE_ROWS.map(()=>BLADE_ROTATION),muzzle:new T.Vector3(),mobility:1,deploy:.26,release:.32},
};

/** Reversible assembly on top of the live flight formation. The underlying
 * springs never stop, so release always returns to the current movement pose. */
export class CrystalWeaponMorph {
  amount = 0;
  /** Which wing-built form this morph assembles. Changing it releases first. */
  shape: CrystalWeaponShape = 'sniper';
  private recoil = 0;
  private spin = 0;
  private spinSpeed = 0;
  private spinHold = 0;
  private readonly spinQ=new T.Quaternion();
  private readonly spinAxis=new T.Vector3(0,0,1);
  private readonly laneSides=new Float32Array(10);
  private readonly bodyInverse = new T.Matrix4();
  private readonly aimToBody = new T.Matrix4();
  private readonly bodyToGroup = new T.Matrix4();
  private readonly source = new T.Matrix4();
  private readonly target = new T.Matrix4();
  private readonly p = new T.Vector3();
  private readonly q = new T.Quaternion();
  private readonly s = new T.Vector3();
  private readonly tp = new T.Vector3();
  private readonly tq = new T.Quaternion();
  private readonly ts = new T.Vector3();
  private readonly local = new T.Vector3();
  private readonly controlA = new T.Vector3();
  private readonly controlB = new T.Vector3();
  private readonly zero = new T.Matrix4().makeScale(0, 0, 0);
  get ready(): boolean { return this.amount > .97 && this.pendingShape === null; }
  get flash(): number { return this.recoil; }
  reset(): void { this.amount = this.recoil = this.spin = this.spinSpeed = this.spinHold = 0; if (this.pendingShape) this.adoptPendingShape(); }
  shoot(): void { if (this.ready) { this.recoil = 1; this.spinHold=.14; } }
  private moduleCount=8;
  private pendingShape:{shape:CrystalWeaponShape;count:number}|null=null;
  private adoptPendingShape():void {
    this.shape=this.pendingShape!.shape;this.moduleCount=this.pendingShape!.count;
    this.pendingShape=null;this.recoil=this.spin=this.spinSpeed=this.spinHold=0;
  }
  /** Shape OR mobility changes release the old assembly before reallocating
   * physical modules; a piece can never exist in two forms at once. */
  setShape(shape:CrystalWeaponShape,mobility=LAYOUTS[shape].mobility):void {
    const count=weaponWingModuleCount(mobility);
    if(shape===this.shape&&count===this.moduleCount){this.pendingShape=null;return;}
    this.pendingShape={shape,count};
    if(this.amount===0)this.adoptPendingShape();
  }
  get modules():readonly number[] {return MODULE_SETS.get(this.moduleCount)!;}
  muzzleWorld(out: T.Vector3, frame: CrystalWeaponFrame): boolean {
    if (!this.ready || this.shape === 'blade') return false;
    out.copy(LAYOUTS[this.shape].muzzle).applyMatrix4(frame.aim);return true;
  }
  update(dt: number, frame: CrystalWeaponFrame, meshes: readonly T.InstancedMesh[], group: T.Object3D): void {
    const step = T.MathUtils.clamp(Number.isFinite(dt) ? dt : 0, 0, .1);
    this.spinHold=Math.max(0,this.spinHold-step);
    const spinning=this.spinHold>0||frame.trigger&&frame.active&&this.amount>.97;
    if(frame.charge!==undefined)this.spinSpeed=36*T.MathUtils.clamp(frame.charge,0,1);
    else this.spinSpeed+=((spinning?36:0)-this.spinSpeed)*(1-Math.exp(-step*(spinning?25:5)));
    this.spin=(this.spin+this.spinSpeed*step)%(Math.PI*2);
    // 0.30 s deploy, 0.34 s return. The zero-slope envelope makes reversals
    // continuous, including a quick RMB tap before all pieces have arrived.
    if (this.pendingShape && this.amount === 0) this.adoptPendingShape();
    const releasing = this.pendingShape !== null;
    const starting=this.amount===0,layout=LAYOUTS[this.shape];
    this.amount = T.MathUtils.clamp(this.amount + (frame.active && !releasing ? step / Math.max(.001,frame.deployTime??layout.deploy) : -step / Math.max(.001,frame.stowTime??layout.release)), 0, 1);
    this.recoil *= Math.exp(-22 * step);
    if (this.amount === 0) return;
    // A form being swapped out keeps its last pose relative to the body.
    this.bodyInverse.copy(frame.body).invert();
    if (!releasing) this.aimToBody.multiplyMatrices(this.bodyInverse, frame.aim);
    this.bodyToGroup.copy(group.matrixWorld).invert().multiply(frame.body);
    for (const i of this.modules) {
      const row = layout.rows[i]!;
      // Stock seats first; rails and the focusing crystal close a beat later.
      const delay = i < 6 ? (2 - i % 3) * .045 : .04;
      const k = T.MathUtils.smootherstep(this.amount, delay, 1);
      meshes[0].getMatrixAt(i, this.source);
      this.source.premultiply(group.matrixWorld).premultiply(this.bodyInverse);
      if(Math.abs(this.source.determinant())<1e-10){
        // A limb may still be streaming (its ordinary instance is zero-scaled).
        // Matrix4.decompose cannot extract a rotation from that hidden matrix.
        this.p.setFromMatrixPosition(this.source);this.q.identity();this.s.setScalar(0);
      }else this.source.decompose(this.p, this.q, this.s);
      // Limb names and mesh handedness need not agree. Keep each piece on the
      // side it actually occupied at deployment, including rapid reversals.
      if(starting)this.laneSides[i]=Math.sign(this.p.x)||(i<3?-1:1);
      const side=this.laneSides[i];
      this.tp.set(row[0] + (i < 6 ? side * .018 * this.recoil : 0), row[1], row[2] + .045 * this.recoil);
      this.tq.copy(layout.rotations[i]);
      this.ts.set(row[3], row[4], row[5]);
      if(this.shape==='minigun'&&ROTOR_MODULES.includes(i)){
        const a=ROTOR_MODULES.indexOf(i)*Math.PI/3+this.spin;
        this.tp.set(Math.cos(a)*MINIGUN_ROTOR_RADIUS,.105+Math.sin(a)*MINIGUN_ROTOR_RADIUS,row[2]+.022*this.recoil);
        this.spinQ.setFromAxisAngle(this.spinAxis,this.spin);
        this.tq.premultiply(this.spinQ);
      }
      this.target.compose(this.tp, this.tq, this.ts).premultiply(this.aimToBody).decompose(this.tp, this.tq, this.ts);
      // Lift the limb pieces into the same shoulder-height side lanes as the
      // back blades. Both handles stay OUTSIDE the torso; the old low first
      // handle let the shin pieces swing underneath the hips into the gun.
      if(this.shape==='blade'){
        // The blade can be anywhere the arm is. Leave the back rearward and
        // arrive from outside the body along the blade's own line, rather
        // than through the gun's shoulder lanes (which fling pieces overhead
        // when the arm is raised).
        this.controlA.set(this.p.x+side*.12,this.p.y+.04,Math.max(this.p.z,0)+.30);
        this.local.set(this.tp.x,0,this.tp.z);
        if(this.local.lengthSq()<1e-4)this.local.set(side,0,0);
        this.controlB.copy(this.local.normalize()).multiplyScalar(.32).add(this.tp);
      }else{
        const laneHeight=Math.max(1.36,this.p.y,this.tp.y+.12);
        this.controlA.set(side*Math.max(.90,Math.abs(this.p.x)+.24),laneHeight+.16,this.p.z);
        this.controlB.set(side*Math.max(.78,Math.abs(this.tp.x)+.48),laneHeight,Math.min(-.60,this.tp.z-.20));
      }
      const a = 1 - k;
      this.local.copy(this.p).multiplyScalar(a * a * a)
        .addScaledVector(this.controlA, 3 * a * a * k)
        .addScaledVector(this.controlB, 3 * a * k * k).addScaledVector(this.tp, k * k * k);
      this.q.slerp(this.tq, k);this.s.lerp(this.ts, k);
      this.target.compose(this.local, this.q, this.s).premultiply(this.bodyToGroup);
      for (let part = 0; part < 4; part++) meshes[part].setMatrixAt(i, this.target);
      // Shut propulsion off during assembly; the crystal remains luminous.
      meshes[4].getMatrixAt(i, this.source);
      if(k===1||Math.abs(this.source.determinant())<1e-10)meshes[4].setMatrixAt(i,this.zero);
      else{
        this.source.decompose(this.p,this.q,this.s);this.s.multiplyScalar(1-k);
        this.source.compose(this.p,this.q,this.s);meshes[4].setMatrixAt(i,this.source);
      }
    }
  }
}
